import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import _ from 'lodash';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import useHydratedBeds from '../../../../hooks/useHydrateBeds';
import {BMWardBeds, BMWardId} from '../../../../../../store/reducers/bedManagement';
import AgyleDictionaryField from '../../../../../../components/ui/selector/agyleDictionaryField';
import {useBedManagementStyles} from '../../../../bmStyle';
import SvgIcon from '@mui/material/SvgIcon';
import {agylePref} from '../../../../../../store/reducers/preferences';
import InformationBox from '../../../../../../components/other/informationBox';
import {getBMColumnsForTable} from '../../../../bmData';
import BedManagementChipButton from '../../bedManagementChipButton';
import ExtendedComponentWidget from '../bmExtendedComponentWidget';
import {
    getBeds,
    getBedsBasedOnStatus, getBedWaitValue,
    getPercentageValue,
    getSortFilterLine, handleSortBedByPatientValues,
    renderSelectOption,
    STATUSES,
} from '../../../../bmUtils';
import Grid from '@mui/material/Grid';
import BedManagementTable from '../../bedManagementTable';
import AgyleSelectField from '../../../../../../components/ui/selector/agyleSelectField';

const useStyles = makeStyles(() => ({
    root: {
        padding: 10,
        height: '100%',
        width: '100%'
   },
    fullHeight: {
        height: '100vh',
    },
    widgets: {
        display: 'flex',
        flexWrap: 'wrap',
        marginTop: 0,
    },
    filterContainer: {
        margin: "20px 0px",
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 10,
    },
    filterChips: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
}));

export const SORT_BY_OPTIONS = [
    {id: 0, __description: "Name: Ascending", value: "name", direction: 'asc', default: true, name: 'name'},
    {id: 1, __description: "Name: Descending", value: "name", direction: 'desc', default: false, name: 'name'},
    {id: 2, __description: "Date: Latest", value: "date", direction: 'desc', default: false, name: 'date'},
    {id: 3, __description: "Date: Oldest", value: "date", direction: 'asc', default: false, name: 'date'},
]

export const DescIcon = (props) => (
    <SvgIcon width="24px" height="24px" viewBox="0 0 24 24" fill="white"{...props}>
        <path d="m6 20 4-4H7V4H5v12H2zm5-12h9v2h-9zm0 4h7v2h-7zm0-8h11v2H11zm0 12h5v2h-5z" />
    </SvgIcon>
);

export const AscIcon = (props) => {
    return (
        <SvgIcon width="24" height="24" viewBox="0 0 24 24" {...props}>
            <path d="M11 9h9v2h-9zm0 4h7v2h-7zm0-8h11v2H11zm0 12h5v2h-5zm-6 3h2V8h3L6 4 2 8h3z" />
        </SvgIcon>
    );
};

const INIT_SUMMARY_STATE = {
    ready: [],
    cleaning: [],
    preAssigned: [],
    closed: [],
    occupied: [],
};

const ExtendedAvailableWidget = () => {
    const classesLocal = useStyles();
    const bmClasses = useBedManagementStyles();
    const { t } = useTranslation();

    const agylePreference = useSelector(agylePref);
    const selectedWard = useSelector(BMWardId);

    const [columns, setColumns] = useState([]);
    const [filter, setFilter] = useState('ready');
    const [sortBy, setSortBy] = useState(SORT_BY_OPTIONS[0]);
    const [summaryState, setSummaryState] = useState(INIT_SUMMARY_STATE);

    const {hydratedBeds, bedsLoadingStatus} = useHydratedBeds({selectedWard});

    const stickyColumnIndex = agylePreference?.bedManagement?.tableColumns?.bedTableSticky[filter] || 3;

    useEffect(() => {
        // const tableOrder = agylePreference?.bedManagement?.tableColumns?.['bed'] || [];
        setColumns(getBMColumnsForTable(t, 'bed', filter));
    }, [agylePreference, filter]);

    useEffect(() => {
        if (!bedsLoadingStatus) {
            const filtered = getBedsBasedOnStatus(hydratedBeds, ...Object.keys(INIT_SUMMARY_STATE));
            setSummaryState(filtered);
        }
    }, [hydratedBeds, bedsLoadingStatus]);

    const handleChangeFilter = (filter) => setFilter(filter);

    const handleGetBedCount = (status) => {
        const beds = getBeds(hydratedBeds, status);

        return beds.length;
    }

    const handleChangeSortBy = (sort) => {
        setSortBy(sort);
    };

    const handleFilter = (data) => {
        switch (filter) {
            case "ready":
                return data?.ready || [];
            case "cleaning":
                return data?.cleaning || [];
            case "pre-assigned":
                return data?.preAssigned || [];
            case "closed":
                return data?.closed || [];
            default:
                return data;
        }
    }

    const getTableData = () => {
        const filtered = handleFilter(summaryState);
        return sortBy ? handleSortBedByPatientValues(sortBy?.value, sortBy?.direction, filtered) : filtered;
    }

    const getTableHeight = () => {
        const titleHeight = document.getElementById('availableTitle')?.clientHeight || 0;
        const widgetsHeight = document.getElementById('availableWidgets')?.clientHeight || 0;
        const chipsHeight = document.getElementById('availableChips')?.clientHeight || 0;
        return `calc(100% - ${titleHeight + widgetsHeight + chipsHeight + 50}px)`;
    }

    const renderCtxTable = () => {
        const dataToDisplay = getTableData();
        return (
            _.isEmpty(dataToDisplay) ?
                <div style={{marginRight: 5}}>
                    <InformationBox type={'info'}>
                        {'There is no data to display.'}
                    </InformationBox>
                </div> :
                <BedManagementTable
                    tableHeight={() => getTableHeight()}
                    data={dataToDisplay}
                    columns={columns}
                    stickColumns={stickyColumnIndex}
                />
        );
    };

    return (
        <Grid container className={bmClasses.root} sx={{ p: 2 }}>
            <Typography variant="h6" id="availableTitle">
                {t("Available")}
            </Typography>

            <Grid container item xs={12} spacing={2} className={classesLocal.widgets} id="availableWidgets">
                <Grid item xs={12/5}>
                    <ExtendedComponentWidget title={'Ready'} value={handleGetBedCount(STATUSES.AVAILABLE)}/>
                </Grid>
                <Grid item xs={12/5}>
                    <ExtendedComponentWidget title={'Cleaning'} value={handleGetBedCount(STATUSES.CLEANING)}/>
                </Grid>
                <Grid item xs={12/5}>
                    <ExtendedComponentWidget title={'Pre-Assigned'} value={handleGetBedCount(STATUSES.PREASSIGNED)}/>
                </Grid>
                <Grid item xs={12/5}>
                    <ExtendedComponentWidget title={'Closed'} value={handleGetBedCount(STATUSES.CLOSED)}
                                             percentageValue={getPercentageValue(handleGetBedCount(STATUSES.CLOSED), hydratedBeds.length)}/>
                </Grid>
                <Grid item xs={12/5}>
                    <ExtendedComponentWidget title={'Occupied'} value={handleGetBedCount(STATUSES.OCCUPIED)}
                                             percentageValue={getPercentageValue(handleGetBedCount(STATUSES.OCCUPIED), hydratedBeds.length)}/>
                </Grid>
            </Grid>

            <Grid container item className={classesLocal.filterContainer} id="availableChips">
                <Grid item xs={12} md={8} className={classesLocal.filterChips}>
                    <BedManagementChipButton
                        label={t('Ready')}
                        onClick={() => handleChangeFilter("ready")}
                        active={filter === 'ready'}
                    />

                    <BedManagementChipButton
                        label={t('Cleaning')}
                        onClick={() => handleChangeFilter("cleaning")}
                        active={filter === 'cleaning'}
                    />

                    <BedManagementChipButton
                        label={t('Pre-Assigned')}
                        onClick={() => handleChangeFilter("pre-assigned")}
                        active={filter === 'pre-assigned'}
                    />

                    <BedManagementChipButton
                        label={t('Closed')}
                        onClick={() => handleChangeFilter("closed")}
                        active={filter === 'closed'}
                    />
                </Grid>

                <Grid item xs={12} md={2} style={{maxWidth: 350}}>
                    <AgyleSelectField
                        onChange={handleChangeSortBy}
                        customObject={getSortFilterLine.bind(this, classesLocal)}
                        options={SORT_BY_OPTIONS}
                        value={sortBy}
                        viewRender={renderSelectOption}
                        placeholder={t('Sort by')}
                        noBorder={false}
                        menuClasses={classesLocal.selectField}
                    />
                </Grid>
            </Grid>

            <Grid item xs={12} >
                {renderCtxTable()}
            </Grid>
        </Grid>
    )
}

export default ExtendedAvailableWidget;